"use client";

import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Key, Shield, PenTool } from "lucide-react";
export default function ThePrivacyPromise() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: subHeadlineRef,
    isVisible: subHeadlineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: section1Ref,
    isVisible: section1Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: section2Ref,
    isVisible: section2Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: section3Ref,
    isVisible: section3Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: section4Ref,
    isVisible: section4Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  return <section id="the-privacy-promise" className="py-16 md:py-24 bg-[#D4C4A8]">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex flex-col items-center text-center space-y-12">
          <h2 ref={headlineRef} className="font-display transition-all duration-[500ms] ease-out opacity-100 translate-y-0 !font-sans !font-bold !text-[40px] !text-[hsl(46,65%,52%)]">The Covenant (Privacy Policy)</h2>

          <div ref={subHeadlineRef} className={`bg-primary-light border-l-4 border-primary px-6 py-4 rounded-lg max-w-2xl transition-all duration-[500ms] ease-out delay-100 ${subHeadlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
            <p className="font-display !font-sans !font-medium !text-3xl !text-[hsl(210,100%,12%)]">My Sacred Commitment to Your Privacy</p>
          </div>
           <p className="font-[var(--font-libre-baskerville)] text-[18px] leading-[1.6] text-[hsl(210,100%,12%)]">In the architecture of a legacy, trust is the cornerstone. I do not merely handle data; I am the temporary custodian of your family's most precious intellectual property. My commitment to your privacy is governed by three non-negotiable laws:            </p>
          

          {}
          <div ref={section1Ref} className={`w-full max-w-3xl space-y-6 transition-all duration-[500ms] ease-out delay-150 ${section1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
            <div className="flex items-center justify-center gap-4">
              <Key className="w-8 h-8 flex-shrink-0" style={{
              color: "hsl(210, 100%, 12%)"
            }} />
              <h1 className="font-[var(--font-cinzel)] font-bold tracking-[2px] leading-[1.3] !text-2xl !text-[hsl(46,65%,52%)]">Absolute Sovereignty</h1>
            </div>
            <p className="font-[var(--font-libre-baskerville)] text-[18px] leading-[1.6] text-[hsl(210,100%,12%)]">My commitment to your privacy is more than a promise; it is built into my technical architecture. To ensure narrative precision without compromise, I employ secure, isolated AI environments for synthesis. Your family’s data never enters a public training model, and upon final delivery, all project data is permanently purged. Your wisdom remains exclusively yours—secure, private, and sovereign.</p>
          </div>

          {}
          <div ref={section2Ref} className={`w-full max-w-3xl space-y-6 transition-all duration-[500ms] ease-out delay-200 ${section2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
            <div className="flex items-center justify-center gap-4">
              <Shield className="w-8 h-8 flex-shrink-0" style={{
              color: "hsl(210, 100%, 12%)"
            }} />
              <h1 className="font-[var(--font-cinzel)] font-bold tracking-[2px] leading-[1.3] !text-2xl !text-[hsl(46,65%,52%)]">
                The Privacy Purge
              </h1>
            </div>
            <p className="font-[var(--font-libre-baskerville)] text-[18px] leading-[1.6] text-[hsl(210,100%,12%)]">
              I believe a legacy should have only one gatekeeper: you. Following the 12-month Sovereign Handover period, I perform a permanent system purge of all master files and recordings. I do not store your history in perpetuity; I anchor it with you, then I step away.
            </p>
          </div>

          {}
          <div ref={section3Ref} className={`w-full max-w-3xl space-y-6 transition-all duration-[500ms] ease-out delay-250 ${section3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
            <div className="flex items-center justify-center gap-4">
              <PenTool className="w-8 h-8 flex-shrink-0" style={{
              color: "hsl(210, 100%, 12%)"
            }} />
              <h1 className="font-[var(--font-cinzel)] font-bold tracking-[2px] leading-[1.3] !text-2xl !text-[hsl(46,65%,52%)]">
                Ironclad Discretion
              </h1>
            </div>
            <p className="font-[var(--font-libre-baskerville)] text-[18px] leading-[1.6] text-[hsl(210,100%,12%)]">
              My work is conducted under strict non-disclosure. Your stories are never used for marketing, never shared with third parties, and never stored on public-facing servers. Your legacy remains where it belongs—within your family.
            </p>
          </div>

          {}
          <div ref={section4Ref} className={`w-full max-w-3xl space-y-6 transition-all duration-[500ms] ease-out delay-300 ${section4Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
            
          </div>

          {}
          <div className="w-full max-w-3xl pt-08 pb-08 flex flex-col items-center">
            <div className="w-32 h-px bg-[hsl(210,100%,12%)] mb-8"></div>
            <p className="font-[var(--font-cinzel)] italic text-[18px] text-center leading-[1.6] max-w-2xl !text-[hsl(210,100%,12%)] !font-bold">Your family wisdom legacy is meticulously designed and curated with love, so your voice remains a presence in their lives forever.</p>
          </div>
        </div>
      </div>
    </section>;
}